import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axios from "axios";
import "./standardQuantityPopup.css";

const predefinedItems = [
    "Bedsheet",
    "Duvet Cover",
    "Pillowcase",
    "Bath Towel",
    "Hand Towel",
    "Face Towel",
    "Bathmat"
];

function StandardQuantityPopup({
    propertyId,
    initialQuantities,
    bedrooms,
    singleBeds,
    doubleBeds,
    superKingBeds,
    isBeachTowel,
    onSave,
    onClose
}) {
    const [standardQuantities, setStandardQuantities] = useState([]);
    const [availableItems, setAvailableItems] = useState([]);
    const [selectedExtraItem, setSelectedExtraItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");

    const isEditMode = useMemo(() => !!propertyId, [propertyId]);

    const computeDefaultQuantities = useCallback(() => {
        const s = Number(singleBeds) || 0;
        const d = Number(doubleBeds) || 0;
        // Super King: same quantities as a double, its own sheet/cover sizes.
        const sk = Number(superKingBeds) || 0;
        const b = Number(bedrooms);
        const bedCountForBathmats = Number.isFinite(b) ? b : 0;

        const counts = {
            "Bedsheet": 0,
            "Duvet Cover": 0,
            "Pillowcase": 0,
            "Bath Towel": 0,
            "Hand Towel": 0,
            "Face Towel": 0,
            "Bathmat": 0,
        };

        // ✅ Only create these if there are single beds
        if (s > 0) {
            counts["Small Bedsheet"] = 0;
            counts["Small Duvet Cover"] = 0;

            counts["Small Bedsheet"] += s * 1;
            counts["Small Duvet Cover"] += s * 1;
        }

        // Single bed towels + pillowcases still apply
        counts["Pillowcase"] += s * 2;
        counts["Bath Towel"] += s * 1;
        counts["Hand Towel"] += s * 1;
        counts["Face Towel"] += s * 1;

        // Double beds
        counts["Bedsheet"] += d * 1;
        counts["Duvet Cover"] += d * 1;
        counts["Pillowcase"] += d * 4;
        counts["Bath Towel"] += d * 2;
        counts["Hand Towel"] += d * 2;
        counts["Face Towel"] += d * 2;

        // Super King beds — double quantities, Super-King-size linen
        if (sk > 0) {
            counts["Super King Bedsheet"] = sk * 1;
            counts["Super King Duvet Cover"] = sk * 1;
        }
        counts["Pillowcase"] += sk * 4;
        counts["Bath Towel"] += sk * 2;
        counts["Hand Towel"] += sk * 2;
        counts["Face Towel"] += sk * 2;

        // Bathmats: Studio(0)->1, 1br->2, 2br->3 etc.
        counts["Bathmat"] = Math.max(1, bedCountForBathmats + 1);

        const list = Object.entries(counts).map(([laundry_item_name, count]) => ({
            laundry_item_name,
            count,
        }));

        if (!!isBeachTowel) {
            const beachCount = (s * 1) + (d * 2) + (sk * 2);
            list.push({ laundry_item_name: "Beach Towel", count: beachCount });
        }

        return list;
    }, [bedrooms, singleBeds, doubleBeds, superKingBeds, isBeachTowel]);


    useEffect(() => {
        const fetchAvailableItems = async () => {
            try {
                const response = await axios.get("/api/laundry/laundry-items");
                setAvailableItems(
                    response.data.map((item) => ({
                        value: item.name,
                        label: item.name,
                        id: item.id
                    }))
                );
            } catch (error) {
                console.error("Error fetching laundry items:", error);
            }
        };

        fetchAvailableItems();
    }, []);

    useEffect(() => {
        const buildMergedList = (savedQuantities) => {
            const savedMap = (savedQuantities || []).reduce((map, item) => {
                map[item.laundry_item_name] = item;
                return map;
            }, {});

            const merged = predefinedItems.map((name) => {
                if (savedMap[name]) return savedMap[name];
                return {
                    laundry_item_name: name,
                    count: 0
                };
            });

            (savedQuantities || []).forEach((item) => {
                if (!predefinedItems.includes(item.laundry_item_name)) {
                    merged.push(item);
                }
            });

            return merged.sort((a, b) => {
                const ia = predefinedItems.indexOf(a.laundry_item_name);
                const ib = predefinedItems.indexOf(b.laundry_item_name);

                if (ia !== -1 && ib !== -1) return ia - ib;
                if (ia !== -1) return -1;
                if (ib !== -1) return 1;

                return a.laundry_item_name.localeCompare(b.laundry_item_name);
            });
        };

        const fetchStandardQuantities = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/api/properties/property-sq/${propertyId}`);
                const saved = response.data || [];

                // If no saved SQ rows exist, start from defaults
                if (!saved.length) {
                    setStandardQuantities(buildMergedList(computeDefaultQuantities()));
                } else {
                    setStandardQuantities(buildMergedList(saved));
                }
            } catch (error) {
                console.error("Error fetching standard quantities:", error);
                setStandardQuantities(buildMergedList(computeDefaultQuantities()));
            } finally {
                setLoading(false);
            }
        };

        if (isEditMode) {
            fetchStandardQuantities();
        } else {
            const sanitizedInitial = (initialQuantities || []).map((x) => ({
                laundry_item_name: x.laundry_item_name,
                count: Number(x.count) || 0
            }));

            // If new property and nothing saved in state yet, start from defaults
            if (!sanitizedInitial.length) {
                setStandardQuantities(buildMergedList(computeDefaultQuantities()));
            } else {
                setStandardQuantities(buildMergedList(sanitizedInitial));
            }
        }
    }, [propertyId, isEditMode, initialQuantities, computeDefaultQuantities]);

    const handleQuantityChange = (itemName, newCount) => {
        const safe = Number.isFinite(newCount) && newCount >= 0 ? newCount : 0;
        setStandardQuantities((prev) =>
            prev.map((item) =>
                item.laundry_item_name === itemName ? { ...item, count: safe } : item
            )
        );
    };

    const handleAddItem = () => {
        if (!selectedExtraItem) return;

        const exists = standardQuantities.some(
            (item) => item.laundry_item_name === selectedExtraItem.value
        );
        if (exists) {
            setSelectedExtraItem(null);
            return;
        }

        setStandardQuantities((prev) => [
            ...prev,
            {
                laundry_item_name: selectedExtraItem.value,
                count: 0
            }
        ]);
        setSelectedExtraItem(null);
    };

    const handleRemoveItem = (itemName) => {
        setStandardQuantities((prev) =>
            prev.filter((item) => item.laundry_item_name !== itemName)
        );
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const cleaned = (standardQuantities || []).map((x) => ({
                laundry_item_name: x.laundry_item_name,
                count: Number(x.count) || 0,
            }));

            if (isEditMode) {
                await axios.post(`/api/properties/property-sq/${propertyId}`, {
                    standard_quantities: cleaned,
                });
            }

            // ✅ ALWAYS push back to parent if provided (add + edit mode)
            if (typeof onSave === "function") {
                onSave(cleaned);
            }

            setFeedbackMessage("Standard quantities saved successfully!");
        } catch (error) {
            console.error("Error saving standard quantities:", error);
            setFeedbackMessage("Failed to save standard quantities.");
        } finally {
            setLoading(false);
        }
    };


    const closeFeedback = () => {
        setFeedbackMessage("");
        onClose();
    };

    const filteredOptions = availableItems.filter(
        (opt) => !standardQuantities.some((item) => item.laundry_item_name === opt.value)
    );

    return (
        <div className="sq-popup-overlay">
            <div className="sq-popup-modal">
                <div className="sq-popup-header">
                    <div>
                        <h2 className="sq-popup-title">Manage Standard Quantities</h2>
                        <p className="sq-popup-subtitle">
                            Set default laundry quantities for this property
                        </p>
                    </div>

                    <button type="button" className="modal-x" onClick={onClose}>&times;</button>
                </div>

                {loading || feedbackMessage ? (
                    <div className="sq-popup-loadingWrap">
                        {loading ? (
                            <>
                                <div className="sq-popup-spinner"></div>
                                <p className="sq-popup-feedbackText">Loading...</p>
                            </>
                        ) : (
                            <>
                                <p className="sq-popup-feedbackText">{feedbackMessage}</p>
                                <button
                                    type="button"
                                    onClick={closeFeedback}
                                    className="sq-popup-okBtn"
                                >
                                    OK
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="sq-popup-body">
                        <div className="sq-popup-list">
                            {standardQuantities.map((item, index) => (
                                <div key={index} className="sq-popup-row">
                                    <span className="sq-popup-itemName">
                                        {item.laundry_item_name}
                                    </span>

                                    <input
                                        className="sq-popup-input"
                                        type="number"
                                        min="0"
                                        value={item.count}
                                        onChange={(e) =>
                                            handleQuantityChange(
                                                item.laundry_item_name,
                                                Number(e.target.value)
                                            )
                                        }
                                        placeholder="0"
                                    />

                                    <button
                                        type="button"
                                        className="sq-popup-removeBtn"
                                        onClick={() => handleRemoveItem(item.laundry_item_name)}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="sq-popup-extraRow">
                            <Select
                                options={filteredOptions}
                                onChange={setSelectedExtraItem}
                                value={selectedExtraItem}
                                placeholder="Select Extra Item"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
                                }}
                            />
                            <button
                                type="button"
                                className="sq-popup-addBtn"
                                onClick={handleAddItem}
                            >
                                + Add Item
                            </button>
                        </div>

                        <div className="sq-popup-footer">
                            <button
                                type="button"
                                onClick={onClose}
                                className="sq-popup-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className="sq-popup-btn sq-popup-btnPrimary"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StandardQuantityPopup;
